// The city's 71 district centroids, and the name a moved centre takes from them
// (SPEC-viewfinder §1.5).
//
// It sits beside `moveCentre` because `moveCentre` is the one writer of a picked
// centre and now the one namer of it: a drag, the empty-ring sentence's tap and a
// `sel=` beyond the ring all arrive here, and `axis/` may not import `wheel/`
// (ADR-0013), which is why the rule used to hold for the drag only (#312).
//
// Pure: `shared/geo.ts` and the JSON, nothing of `state/`'s own singletons — so
// `wheel/districts.ts` and this file's tests read it without a browser.
import { haversine } from '../shared/geo.ts';
import districts from './districts.json' with { type: 'json' };

export type District = { name: string; lat: number; lon: number };

/** Every district, read out of the OpenMapTiles `place` layer by a script. */
export const DISTRICTS: District[] = districts;

/**
 * How far a centroid may be and still name the point under the viewfinder
 * (owner's ruling 3, SPEC-viewfinder §1.5).
 *
 * Measured over the 71 points: nearest-neighbour spacing is min 481 m, median
 * 1 331 m, max 2 691 m, so a name can sit up to about 2,7 km from the centre.
 *
 * It does NOT follow that the fallback only fires out of town, and this comment
 * said so until V2 measured it. The largest spacing BETWEEN centroids does not
 * bound the set's COVERING radius: 39,5 % of the pannable box and 13,8 % of the
 * centroids' own bounding box is further than 3 km from every one of them, the
 * worst point 12 086 m out, and the nearest such point a resident can reach is
 * 6,8 km from Stary Rynek. „wybrane miejsce" is an ordinary outskirts reading
 * (SPEC-viewfinder §1.5, and `districts.test.ts` sweeps the box for it).
 */
const NAMES_WITHIN = 3000;

/**
 * What the kreska calls a centre the resident moved to: the nearest district,
 * or `null` past {@link NAMES_WITHIN}.
 *
 * `null` rather than „wybrane miejsce", on purpose: that string is
 * `moveCentre`'s own fallback (`commands.ts`), which is also what „Stąd" on a
 * detail writes, so spelling it here would be a second copy of one rule.
 *
 * It names a DISTRICT and not a street, and that is the ruling rather than a
 * shortfall: a street would need reverse geocoding, which is a new source with
 * new politeness rules (§1.5). Residents name places here this way, and
 * „◌ Chartowo · teraz 14:00" is what the kreska then reads.
 *
 * Pure and O(71) — computed on a centre move, never per frame (§1.5).
 */
export function nearestPlaceName(point: { lat: number; lon: number }): string | null {
  let name: string | null = null;
  let nearest = Infinity;
  for (const d of DISTRICTS) {
    const away = haversine(point, d);
    // Strictly nearer, so a tie keeps the first — and `districts.json` is
    // sorted by name, which makes „the first" the Polish-alphabetical one and
    // the answer the same on every run.
    if (away < nearest) {
      nearest = away;
      name = d.name;
    }
  }
  // „further than 3 km" is the ruling, so 3 km exactly still names the place.
  return nearest <= NAMES_WITHIN ? name : null;
}
